import { Component, AfterViewInit, ElementRef, OnDestroy, OnInit, ViewChild, TemplateRef, Injector, effect, inject } from '@angular/core';

import { RouterModule } from '@angular/router';
import { ChatComponent, SplitPaneComponent } from '@components';
import { SplitPaneVerticalComponent, HistoryComponent } from '@components';
import { AiOrchestratorResult } from '@components';
import { SkeletonBlockComponent } from '@components';
import { Graph } from '@antv/x6';
import { DagreLayout } from '@antv/layout';
import { register as registerAngularShape } from '@antv/x6-angular-shape';
import { DbDesignFacade } from '@state';
import { ToastService } from '@services';
import { applyJsonPatchOps, JsonPatchOp } from '../../utils/json-patch';

@Component({
  selector: 'app-db-design',
  templateUrl: './db-design.page.html',
  styleUrls: ['./db-design.page.scss'],
  standalone: true,
  imports: [
    RouterModule,
    ChatComponent,
    HistoryComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
    SkeletonBlockComponent
],
})
export class DbDesignPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('erCanvas', { static: true }) private erCanvasRef!: ElementRef<HTMLDivElement>;
  @ViewChild('erNode', { static: true }) private erNodeTpl!: TemplateRef<any>;
  // Use template ref `#dbDesignChat` in the template.
  @ViewChild('dbDesignChat') public dbDesignChat?: ChatComponent;
  private graph?: Graph;
  private resizeObserver?: ResizeObserver;
  private resizeFitTimer: any = null;
  private graphResizeRaf: number | null = null;
  public dbDesignStore = inject(DbDesignFacade);
  private toast = inject(ToastService);
  private loadedForProjectId: string | null = null;
  private layoutSaveTimer: any = null;

  constructor(private injector: Injector) {
    // Must be created in an injection context (constructor is OK).
    effect(() => {
      const pid = this.dbDesignStore.activeProjectId();
      if (!pid) return;
      if (this.loadedForProjectId === pid) return;
      this.loadedForProjectId = pid;
      // Prevent cross-project diagram bleed: clear immediately on project switch.
      this.clearDiagram();
      // load() dispatches async doc loads; schema() updates reactively. Render once
      // it's available (not an effect() — runs inside a reactive context, NG0602).
      this.dbDesignStore.load();
      this.renderSchemaWhenReady();
    });
  }

  private renderSchemaWhenReady(attempt = 0): void {
    if (!this.dbDesignStore.loading()) {
      const schema = this.dbDesignStore.schema();
      if (schema) this.renderSchema(schema);
      else this.clearDiagram();
      return;
    }
    if (attempt >= 80) return;
    setTimeout(() => this.renderSchemaWhenReady(attempt + 1), 75);
  }

  public dbDesignSuggestions: any[] = [
    { icon: 'bi bi-diagram-3', prompt: 'Draft an ER diagram for my app' },
    { icon: 'bi bi-table', prompt: 'Propose tables and fields for users and roles' },
    { icon: 'bi bi-key', prompt: 'Suggest primary and foreign keys for these entities' },
    { icon: 'bi bi-boxes', prompt: 'Normalize this schema to 3NF' },
    { icon: 'bi bi-lightning', prompt: 'Denormalize for analytics and reporting' },
    { icon: 'bi bi-shield-lock', prompt: 'Recommend security and PII handling in the schema' },
  ];

  public dbDesignContext: any = {
    page: 'db-design',
    purpose: 'Assist with database modeling, normalization and performance considerations',
    entities: [],
    relationships: [],
    keys: { primary: [], foreign: [] },
    indexes: [],
    constraints: [],
    piiHandling: [],
    outputs: ['ER diagram', 'DDL/migrations', 'Entity list with fields']
  };

  get debugEnabled(): boolean {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('debugDefine') === '1';
    } catch {
      return false;
    }
  }

  get debugSchemaSummary(): { tables: number; relationships: number; tableNames: string[] } {
    const schema = this.dbDesignStore.schema();
    const tables = Array.isArray(schema?.tables) ? schema.tables : [];
    const relationships = Array.isArray(schema?.relationships) ? schema.relationships : [];
    const tableNames = tables
      .map((t: any) => String(t?.name ?? '').trim())
      .filter(Boolean)
      .slice(0, 12);
    return { tables: tables.length, relationships: relationships.length, tableNames };
  }

  ngOnInit(): void {}

  public onGenerateFromRepo() {
    const prompt = [
      'Generate (or refine) the DB schema from all connected GitHub repos.',
      '',
      'Requirements:',
      '- Treat pageContext.repoContext.github.repos as source of truth.',
      '- You MUST inspect every connected repo in pageContext.repoContext.github.repos.',
      '- For EACH repo: call github_get_tree first, then read migrations/models/schema evidence with github_get_content (prisma/schema.prisma, migrations/, entities/, schema.sql, openapi).',
      '- Output a complete /schema patch.',
      '- Include evidence grouped per repo/role in your message (site/api/agent-api or owner/repo groups).',
      '- If fewer than 2 repos are inspected while multiple repos are connected, return patches:[] and ask to retry.',
    ].join('\n');
    this.dbDesignChat?.sendInjectedPrompt(prompt);
  }

  get hasActiveDbDesignChat(): boolean {
    return (this.dbDesignStore.chatMessages()?.length ?? 0) > 0;
  }

  get canShowGenerateFromRepoButton(): boolean {
    const github = this.dbDesignStore.repoContext()?.github;
    const hasInstallation = (github?.installationId ?? null) !== null;
    const hasRepos = (github?.repos?.length ?? 0) > 0;
    return hasInstallation && hasRepos && !this.hasActiveDbDesignChat;
  }

  get dbDesignOrchestratorContext(): any {
    const definition = this.dbDesignStore.projectDefinition() ?? {};
    const schema = this.dbDesignStore.schema() ?? null;
    const repoContext = this.dbDesignStore.repoContext()?.github;
    return {
      definition: {
        name: String((definition as any)?.name ?? ''),
        description: String((definition as any)?.description ?? '').slice(0, 500),
        goals: Array.isArray((definition as any)?.goals) ? (definition as any).goals.slice(0, 8) : [],
        features: Array.isArray((definition as any)?.features) ? (definition as any).features.slice(0, 10) : [],
      },
      schema: schema
        ? {
            tables: Array.isArray((schema as any).tables) ? (schema as any).tables.slice(0, 80) : [],
            relationships: Array.isArray((schema as any).relationships) ? (schema as any).relationships.slice(0, 120) : [],
          }
        : null,
      repoContext: repoContext
        ? {
            github: {
              installationId: repoContext.installationId ?? null,
              repos: Array.isArray(repoContext.repos) ? repoContext.repos.slice(0, 10) : [],
            },
          }
        : null,
    };
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.dbDesignStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.dbDesignStore.logChatEvent(msg.text, msg.role);
  }

  async onAiResult(result: AiOrchestratorResult) {
    if (!result || String(result.page ?? '').toLowerCase() !== 'db-design') return;
    const opsRaw = (Array.isArray(result.patches) ? result.patches : []) as JsonPatchOp[];
    const ops = [...opsRaw];
    if (ops.length === 0) {
      // Recovery path: some model responses include `schema` but forget `patches`.
      const raw = (result as any)?.raw;
      const directSchemaCandidate =
        raw && typeof raw === 'object'
          ? ((raw as any).schema ??
            ((raw as any).data && typeof (raw as any).data === 'object' ? (raw as any).data.schema : null) ??
            (Array.isArray((raw as any).tables) || Array.isArray((raw as any).relationships) ? raw : null))
          : null;
      if (directSchemaCandidate) {
        ops.push({
          op: 'replace',
          path: '/schema',
          value: directSchemaCandidate as any,
        } as JsonPatchOp);
      }
    }
    if (ops.length === 0) return;

    const currentSchema =
      this.dbDesignStore.schema() ??
      ({
        tables: [],
        relationships: [],
      } as any);

    try {
      const normalizedOps = ops.map((op: any) => {
        const path = String(op?.path ?? '');
        // Normalize if agent ever emits /tables... or /relationships... instead of /schema/...
        if (path === '/tables' || path.startsWith('/tables/')) return { ...op, path: `/schema${path}` };
        if (path === '/relationships' || path.startsWith('/relationships/')) return { ...op, path: `/schema${path}` };
        if (path === '/dbSchema' || path === '/databaseSchema' || path === '/schemaDraft') {
          return { ...op, path: '/schema' };
        }
        return op;
      }) as JsonPatchOp[];

      const wrappedNext = applyJsonPatchOps<{ schema: any }>({ schema: currentSchema }, normalizedOps);
      const nextSchemaRaw = wrappedNext?.schema ?? null;
      const nextSchema = this.normalizeSchema(nextSchemaRaw);

      // Basic validation: ensure we got a meaningful multi-table schema.
      const currentCount = Array.isArray(currentSchema?.tables) ? currentSchema.tables.length : 0;
      const nextCount = Array.isArray(nextSchema?.tables) ? nextSchema.tables.length : 0;
      if (nextCount < 2) {
        this.toast.error(`AI returned only ${nextCount} table(s). Please try again.`, 'DB Design');
        return;
      }
      if (currentCount >= 2 && nextCount < currentCount) {
        this.toast.error(`AI reduced table count from ${currentCount} to ${nextCount}. Please try again.`, 'DB Design');
        return;
      }

      const { schema: validatedSchema, droppedRelationships } = this.validateAndPruneRelationships(nextSchema);

      await this.dbDesignStore.saveSchema(validatedSchema);
      if (this.graph && validatedSchema) this.renderSchema(validatedSchema);
      if (droppedRelationships > 0) {
        this.toast.warning(`Dropped ${droppedRelationships} invalid relationship(s) (missing tables/fields).`, 'DB Design');
      }
      this.toast.success('AI updated DB schema', 'DB Design');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to apply DB schema updates', 'DB Design');
    }
  }

  private normalizeSchema(raw: any): { tables: any[]; relationships: any[] } {
    const base = raw && typeof raw === 'object' ? raw : {};
    const candidate = base?.schema && typeof base.schema === 'object' ? base.schema : base;

    const asArray = (v: any) => (Array.isArray(v) ? v : []);
    const toStr = (v: any) => String(v ?? '').trim();
    const key = (v: any) => toStr(v).toLowerCase();

    const normalizeField = (f: any) => {
      if (typeof f === 'string') return { name: toStr(f), type: '' };
      if (Array.isArray(f)) return { name: toStr(f[0]), type: toStr(f[1]) };
      const name = toStr(f?.name ?? f?.columnName ?? f?.fieldName ?? f?.title ?? '');
      const type = toStr(f?.type ?? f?.dataType ?? f?.fieldType ?? f?.columnType ?? '');
      if (!name) return null;
      return {
        name,
        type,
        pk: !!f?.pk || !!f?.primaryKey,
        fk: !!f?.fk || !!f?.foreignKey,
        nullable: f?.nullable === undefined ? undefined : !!f?.nullable,
        unique: f?.unique === undefined ? undefined : !!f?.unique,
      };
    };

    const normalizeTable = (t: any) => {
      if (typeof t === 'string') return { name: toStr(t), fields: [] };
      const name = toStr(t?.name ?? t?.tableName ?? t?.title ?? '');
      if (!name) return null;
      const fieldsRaw = asArray(t?.fields ?? t?.columns ?? t?.cols ?? t?.attributes ?? []);
      const fields = fieldsRaw.map(normalizeField).filter(Boolean);
      return { name, fields };
    };

    const normalizeRelationship = (r: any) => {
      if (!r || typeof r !== 'object') return null;
      const fromTable = toStr(r?.fromTable ?? r?.from?.table ?? r?.sourceTable ?? r?.source ?? '');
      const fromField = toStr(r?.fromField ?? r?.from?.field ?? r?.sourceField ?? r?.sourceColumn ?? '');
      const toTable = toStr(r?.toTable ?? r?.to?.table ?? r?.targetTable ?? r?.target ?? '');
      const toField = toStr(r?.toField ?? r?.to?.field ?? r?.targetField ?? r?.targetColumn ?? '');
      if (!fromTable || !toTable) return null;
      const cardinality = toStr(r?.cardinality ?? r?.type ?? '');
      const label = toStr(r?.label ?? '');
      return {
        fromTable,
        fromField,
        toTable,
        toField,
        cardinality: (cardinality as any) || undefined,
        label: label || undefined,
      };
    };

    const tables = asArray(candidate?.tables ?? candidate?.table ?? candidate?.entities ?? candidate?.models)
      .map(normalizeTable)
      .filter((x): x is any => !!x);
    const dedupedTables: any[] = [];
    const seen = new Set<string>();
    for (const t of tables) {
      const k = key(t.name);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      dedupedTables.push(t);
    }

    const relationships = asArray(candidate?.relationships ?? candidate?.relations ?? candidate?.edges ?? candidate?.links)
      .map(normalizeRelationship)
      .filter((x): x is any => !!x);

    return {
      tables: dedupedTables,
      relationships,
    };
  }

  private validateAndPruneRelationships(schema: { tables: any[]; relationships: any[] }): { schema: any; droppedRelationships: number } {
    const normalizeKey = (v: any) => String(v ?? '').trim().toLowerCase();
    const tables = Array.isArray(schema?.tables) ? schema.tables : [];
    const relationships = Array.isArray(schema?.relationships) ? schema.relationships : [];
    const tableByKey = new Map<string, any>();
    for (const t of tables) {
      const k = normalizeKey(t?.name);
      if (!k) continue;
      tableByKey.set(k, t);
    }

    let dropped = 0;
    const nextRelationships = relationships.filter((r: any) => {
      const ft = normalizeKey(r?.fromTable);
      const tt = normalizeKey(r?.toTable);
      if (!ft || !tt) {
        dropped++;
        return false;
      }
      const fromTable = tableByKey.get(ft);
      const toTable = tableByKey.get(tt);
      if (!fromTable || !toTable) {
        dropped++;
        return false;
      }
      const ff = normalizeKey(r?.fromField);
      const tf = normalizeKey(r?.toField);
      // If fields are provided, ensure they exist; otherwise allow relationship as table-level.
      if (ff) {
        const fields = Array.isArray(fromTable?.fields) ? fromTable.fields : [];
        const ok = fields.some((f: any) => normalizeKey(f?.name) === ff);
        if (!ok) {
          dropped++;
          return false;
        }
      }
      if (tf) {
        const fields = Array.isArray(toTable?.fields) ? toTable.fields : [];
        const ok = fields.some((f: any) => normalizeKey(f?.name) === tf);
        if (!ok) {
          dropped++;
          return false;
        }
      }
      return true;
    });

    return { schema: { ...schema, relationships: nextRelationships }, droppedRelationships: dropped };
  }

  private getTheme() {
    const root = getComputedStyle(document.documentElement);
    const varVal = (name: string, fallback: string) => (root.getPropertyValue(name) || '').trim() || fallback;
    const colors = {
      panelBg: varVal('--color-surface', '#ffffff'),
      text: varVal('--text-primary', '#0b1f35'),
      muted: varVal('--text-muted', '#64748b'),
      border: varVal('--color-border', '#e5e7eb'),
      edge: varVal('--color-border', '#cbd5e1'),
    };
    const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' };
    const accents = ['#1f6feb', '#7f56d9', '#16a34a', '#f97316', '#ef4444', '#0ea5e9', '#a855f7', '#22c55e'];
    return { colors, mono, accents };
  }

  private normalizeTableKey(v: any): string {
    return String(v ?? '').trim().toLowerCase();
  }

  private getCanvasWidthFallback(): number {
    try {
      const w = this.erCanvasRef?.nativeElement?.getBoundingClientRect()?.width;
      if (typeof w === 'number' && Number.isFinite(w) && w > 0) return w;
    } catch {
      // ignore
    }
    return 1024;
  }

  private computeGridPositions(
    specs: Array<{ key: string; width: number; height: number }>,
    canvasWidth: number,
  ): Record<string, { x: number; y: number }> {
    const margin = 40;
    const gapX = 90;
    const gapY = 90;
    const maxWidth = Math.max(1, canvasWidth - margin * 2);
    const firstW = specs[0]?.width ?? 260;
    const cols = Math.max(1, Math.floor((maxWidth + gapX) / (firstW + gapX)));

    const pos: Record<string, { x: number; y: number }> = {};
    let col = 0;
    let x = margin;
    let y = margin;
    let rowMaxH = 0;

    for (const s of specs) {
      pos[s.key] = { x, y };
      rowMaxH = Math.max(rowMaxH, s.height);

      col++;
      if (col >= cols) {
        col = 0;
        x = margin;
        y += rowMaxH + gapY;
        rowMaxH = 0;
      } else {
        x += s.width + gapX;
      }
    }

    return pos;
  }

  private scheduleSaveLayout(positions: Record<string, { x: number; y: number }>): void {
    if (this.layoutSaveTimer) clearTimeout(this.layoutSaveTimer);
    this.layoutSaveTimer = setTimeout(() => {
      this.layoutSaveTimer = null;
      void this.dbDesignStore.saveLayout({ positions });
    }, 350);
  }

  private positionsEqual(
    a: Record<string, { x: number; y: number }>,
    b: Record<string, { x: number; y: number }>,
  ): boolean {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if (!(k in b)) return false;
      const ax = Number(a[k]?.x);
      const ay = Number(a[k]?.y);
      const bx = Number(b[k]?.x);
      const by = Number(b[k]?.y);
      if (!Number.isFinite(ax) || !Number.isFinite(ay) || !Number.isFinite(bx) || !Number.isFinite(by)) return false;
      if (Math.abs(ax - bx) > 0.5 || Math.abs(ay - by) > 0.5) return false;
    }
    return true;
  }

  private renderSchema(schema: any): void {
    if (!this.graph) return;
    const { colors, mono, accents } = this.getTheme();

    try {
      this.graph.clearCells();
    } catch {
      (this.graph as any).clearCells?.();
    }

    const tables = Array.isArray(schema?.tables) ? schema.tables : [];
    const relationships = Array.isArray(schema?.relationships) ? schema.relationships : [];
    if (tables.length === 0) {
      (this.graph as any).centerContent?.();
      return;
    }

    const storedPositions = this.dbDesignStore.layout()?.positions ?? {};
    const canvasWidth = this.getCanvasWidthFallback();

    const createTable = (title: string, fields: Array<{ name: string; type: string; pk?: boolean; fk?: boolean }>, accent: string) => {
      const width = 260;
      const height = 48 + fields.length * 32; // header + rows
      return { shape: 'er-angular-node', width, height, data: { ngArguments: { title, fields, accent } } };
    };

    const nodeByTableKey = new Map<string, any>();
    const nodeWidth = 260;
    const tableSpecs = tables
      .map((t: any, idx: number) => {
        const name = String(t?.name ?? '').trim();
        if (!name) return null;
        const key = this.normalizeTableKey(name);
        const fieldsRaw = Array.isArray(t?.fields) ? t.fields : [];
        const fields = fieldsRaw.map((f: any) => ({
          name: String(f?.name ?? ''),
          type: String(f?.type ?? ''),
          pk: !!f?.pk,
          fk: !!f?.fk,
        }));
        const height = 48 + fields.length * 32;
        const accent = accents[idx % accents.length]!;
        return { name, key, fields, width: nodeWidth, height, accent };
      })
      .filter((x: any): x is any => !!x);

    const missingPositionSpecs = tableSpecs
      .filter((s: any) => {
        const p = storedPositions[s.key];
        const x = Number(p?.x);
        const y = Number(p?.y);
        return !(Number.isFinite(x) && Number.isFinite(y));
      })
      .map((s: any) => ({ key: s.key, width: s.width, height: s.height }));

    const gridPositions = this.computeGridPositions(missingPositionSpecs, canvasWidth);

    tableSpecs.forEach((s: any) => {
      const saved = storedPositions[s.key];
      const sx = Number(saved?.x);
      const sy = Number(saved?.y);
      const grid = gridPositions[s.key];
      const x = Number.isFinite(sx) ? sx : Number(grid?.x ?? 40);
      const y = Number.isFinite(sy) ? sy : Number(grid?.y ?? 40);

      const base = createTable(s.name, s.fields, s.accent) as any;
      // Store the tableKey on the node so we can persist on drag.
      const node = this.graph!.addNode({
        x,
        y,
        ...base,
        data: { ...(base?.data ?? {}), tableKey: s.key },
      } as any);

      nodeByTableKey.set(s.key, node);
    });

    relationships.forEach((r: any) => {
      const fromTable = String(r?.fromTable ?? '').trim();
      const toTable = String(r?.toTable ?? '').trim();
      if (!fromTable || !toTable) return;
      const fromNode = nodeByTableKey.get(this.normalizeTableKey(fromTable));
      const toNode = nodeByTableKey.get(this.normalizeTableKey(toTable));
      if (!fromNode || !toNode) return;

      const labelText = String(r?.cardinality ?? r?.label ?? '').trim();
      const labels = labelText
        ? [
            {
              markup: [{ tagName: 'rect', selector: 'body' }, { tagName: 'text', selector: 'label' }],
              attrs: {
                body: { ref: 'label', refWidth: '120%', refHeight: '120%', refX: '-10%', refY: '-10%', rx: 4, ry: 4, fill: colors.panelBg, stroke: colors.edge, strokeWidth: 1 },
                label: { text: labelText, ...mono, fontSize: 11, fill: colors.text },
              },
            },
          ]
        : [];

      this.graph!.addEdge({
        source: fromNode,
        target: toNode,
        attrs: {
          line: {
            stroke: colors.edge,
            strokeWidth: 1.4,
            targetMarker: { name: 'block', width: 10, height: 8 },
          },
        },
        labels,
      } as any);
    });

    try {
      // Only attempt Dagre when there are edges; with no edges it's common for layout to be a no-op.
      const hasEdges = this.graph.getEdges().length > 0;
      if (hasEdges) {
        const layout = new (DagreLayout as any)({
          rankdir: 'LR',
          nodesep: 80,
          ranksep: 140,
          controlPoints: true,
        });
        const model = {
          nodes: this.graph.getNodes().map((n) => {
            const size = n.size();
            return { id: n.id, width: size.width, height: size.height };
          }),
          edges: this.graph.getEdges().map((e) => ({
            source: e.getSourceCellId(),
            target: e.getTargetCellId(),
          })),
        };
        const result = (layout as any).layout(model);

        const dagreNodes = Array.isArray(result?.nodes) ? result.nodes : [];
        const hasUsableCoords = dagreNodes.some((n: any) => Number.isFinite(n?.x) && Number.isFinite(n?.y));

        // Failure guard: if Dagre didn't return usable coords, keep saved/grid positions.
        if (hasUsableCoords) {
          dagreNodes.forEach((n: any) => {
            const cell = this.graph!.getCellById(String(n.id));
            if (!cell || !('position' in cell)) return;

            // Only apply Dagre positioning to tables that don't already have saved positions.
            const data = (cell as any).getData?.() ?? {};
            const tableKey = this.normalizeTableKey(data?.tableKey ?? data?.ngArguments?.title ?? '');
            const saved = storedPositions[tableKey];
            const sx = Number(saved?.x);
            const sy = Number(saved?.y);
            if (Number.isFinite(sx) && Number.isFinite(sy)) return;

            const nodeSize = (cell as any).size();
            const x = (n.x ?? 0) - nodeSize.width / 2;
            const y = (n.y ?? 0) - nodeSize.height / 2;
            (cell as any).position(x, y);
          });
        }
      }

      this.graph.getEdges().forEach((e) => e.setRouter('orth'));
      (this.graph as any).centerContent?.();
      (this.graph as any).zoomToFit?.({ padding: 40, maxScale: 1 });
    } catch {
      // no-op if layout fails
    }

    // Persist final positions (saved + grid + dagre) so refresh restores layout.
    const nextPositions: Record<string, { x: number; y: number }> = { ...storedPositions };
    for (const [tableKey, node] of nodeByTableKey.entries()) {
      const p = (node as any).getPosition?.() ?? (node as any).position?.();
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      nextPositions[tableKey] = { x, y };
    }
    if (!this.positionsEqual(storedPositions, nextPositions)) {
      this.scheduleSaveLayout(nextPositions);
    }
  }

  private clearDiagram(): void {
    if (!this.graph) return;
    try {
      this.graph.clearCells();
    } catch {
      (this.graph as any).clearCells?.();
    }
    (this.graph as any).centerContent?.();
  }

  private resizeGraphToContainer(): void {
    if (!this.graph || !this.erCanvasRef?.nativeElement) return;
    const container = this.erCanvasRef.nativeElement;
    const { width, height } = container.getBoundingClientRect();
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    try {
      this.graph.resize(w, h);
    } catch {
      (this.graph as any).resize?.(w, h);
    }
    if (this.resizeFitTimer) clearTimeout(this.resizeFitTimer);
    this.resizeFitTimer = setTimeout(() => {
      if (!this.graph) return;
      try {
        const schema = this.dbDesignStore.schema();
        if (schema && this.graph.getNodes().length > 0) {
          this.renderSchema(schema);
          return;
        }
        (this.graph as any).centerContent?.();
        (this.graph as any).zoomToFit?.({ padding: 40, maxScale: 1 });
      } catch {
        // ignore fit failures during intermediate layout states
      }
    }, 60);
  }

  public onDiagramViewportResize(): void {
    if (this.graphResizeRaf !== null) cancelAnimationFrame(this.graphResizeRaf);
    this.graphResizeRaf = requestAnimationFrame(() => {
      this.graphResizeRaf = null;
      this.resizeGraphToContainer();
    });
  }

  ngAfterViewInit(): void {
    const container = this.erCanvasRef?.nativeElement;
    if (!container) return;

    const root = getComputedStyle(document.documentElement);
    const varVal = (name: string, fallback: string) =>
      (root.getPropertyValue(name) || '').trim() || fallback;

    const colors = {
      panelBg: varVal('--color-surface', '#ffffff'),
      text: varVal('--text-primary', '#0b1f35'),
      grid: varVal('--color-border', '#e5e7eb'),
      userStroke: '#1f6feb',
      companyStroke: '#7f56d9',
      beltStroke: '#16a34a',
      edgeCompany: '#7f56d9',
      edgeBelt: '#16a34a',
    };

    this.graph = new Graph({
      container,
      background: { color: 'transparent' },
      grid: {
        size: 10,
        visible: true,
        type: 'dot',
        args: { color: colors.grid, thickness: 1 },
      },
      panning: true,
      mousewheel: {
        enabled: true,
        modifiers: 'ctrl',
        zoomAtMousePosition: true
      },
      connecting: {
        router: 'orth',
        connector: { name: 'rounded', args: { radius: 8 } },
        allowBlank: false,
        allowLoop: false,
        snap: true,
        highlight: true
      },
    });

    // Persist manual node arrangement (dragging).
    const onNodeMoved = ({ node }: any) => {
      if (!node) return;
      const data = node.getData?.() ?? {};
      const tableKey = this.normalizeTableKey(data?.tableKey ?? data?.ngArguments?.title ?? '');
      if (!tableKey) return;
      const p = node.getPosition?.() ?? node.position?.();
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const stored = this.dbDesignStore.layout()?.positions ?? {};
      const next = { ...stored, [tableKey]: { x, y } };
      this.scheduleSaveLayout(next);
    };
    try {
      // X6 event names can vary slightly by build/version; register both defensively.
      (this.graph as any).on?.('node:moved', onNodeMoved);
      (this.graph as any).on?.('node:change:position', onNodeMoved);
    } catch {
      // ignore
    }

    // Keep the X6 canvas responsive when the split pane (or window) resizes.
    // X6 measures the container once at init; we must call `graph.resize(...)` on subsequent size changes.
    this.resizeObserver = new ResizeObserver(() => this.resizeGraphToContainer());
    this.resizeObserver.observe(container);
    // Trigger one resize after initial layout to sync canvas with flex sizing.
    queueMicrotask(() => this.resizeGraphToContainer());

    // Register angular template shape for nodes (X6 v3 API)
    registerAngularShape({
      shape: 'er-angular-node',
      width: 260,
      height: 160,
      injector: this.injector,
      content: this.erNodeTpl,
    });

    // Initial render: use persisted schema if present. Otherwise, show empty state (overlay in template).
    const schema = this.dbDesignStore.schema();
    if (schema) this.renderSchema(schema);
    else this.clearDiagram();
  }

  ngOnDestroy(): void {
    if (this.layoutSaveTimer) clearTimeout(this.layoutSaveTimer);
    if (this.resizeFitTimer) clearTimeout(this.resizeFitTimer);
    if (this.graphResizeRaf !== null) cancelAnimationFrame(this.graphResizeRaf);
    this.resizeObserver?.disconnect();
    this.graph?.dispose();
  }

}


